import { getApiUrl } from "@dashboard/config";
import { createFetch } from "@saleor/sdk";

import { LedgerReportData } from "../types";

export interface DownloadReportOptions {
  format: "pdf" | "csv";
  startDate?: string;
  endDate?: string;
  period?: string;
}

export async function fetchLedgerReportData({
  startDate,
  endDate,
  period,
}: {
  startDate?: string;
  endDate?: string;
  period?: string;
}): Promise<LedgerReportData> {
  const apiUrl = getApiUrl();
  const apiOrigin = new URL(apiUrl, window.location.origin).origin;
  const url = new URL(`${apiOrigin}/api/ledger/reports/`);

  url.searchParams.set("format", "json");

  if (period === "day" && startDate) {
    url.searchParams.set("date", startDate);
  } else if (period === "month" && startDate) {
    url.searchParams.set("month", startDate.slice(0, 7));
  } else {
    if (startDate) {
      url.searchParams.set("start_date", startDate);
    }

    if (endDate) {
      url.searchParams.set("end_date", endDate);
    }
  }

  const authenticatedFetch = createFetch({ refreshOnUnauthorized: false });
  const response = await authenticatedFetch(url.toString(), {
    method: "GET",
    credentials: "include",
  });

  if (!response.ok) {
    let errorMsg = `Failed to load report summary (HTTP ${response.status})`;

    try {
      const errJson = await response.json();

      if (errJson?.error) {
        errorMsg = errJson.error;
      }
    } catch {
      // Default
    }

    throw new Error(errorMsg);
  }

  return response.json();
}

export async function downloadLedgerReport({
  format,
  startDate,
  endDate,
  period,
}: DownloadReportOptions): Promise<void> {
  const apiUrl = getApiUrl();
  const apiOrigin = new URL(apiUrl, window.location.origin).origin;
  const url = new URL(`${apiOrigin}/api/ledger/reports/`);

  url.searchParams.set("format", format);

  if (period === "day" && startDate) {
    url.searchParams.set("date", startDate);
  } else if (period === "month" && startDate) {
    url.searchParams.set("month", startDate.slice(0, 7));
  } else {
    if (startDate) {
      url.searchParams.set("start_date", startDate);
    }

    if (endDate) {
      url.searchParams.set("end_date", endDate);
    }
  }

  // refreshOnUnauthorized: false prevents @saleor/sdk from calling response.clone().json() on binary PDF/CSV files
  const authenticatedFetch = createFetch({ refreshOnUnauthorized: false });
  const response = await authenticatedFetch(url.toString(), {
    method: "GET",
    credentials: "include",
  });

  if (!response.ok) {
    let errorMsg = `Report download failed (HTTP ${response.status})`;

    try {
      const contentType = response.headers.get("content-type");

      if (contentType && contentType.includes("application/json")) {
        const errJson = await response.json();

        if (errJson?.error) {
          errorMsg = errJson.error;
        }
      } else {
        const text = await response.text();

        if (text) {
          errorMsg = text.slice(0, 200);
        }
      }
    } catch {
      // Default message if parsing fails
    }

    throw new Error(errorMsg);
  }

  const blob = await response.blob();
  const disposition = response.headers.get("content-disposition");
  let filename = `sales_report_${startDate || "report"}.${format}`;

  if (disposition && disposition.includes("filename=")) {
    const match = disposition.match(/filename="?([^";]+)"?/);

    if (match && match[1]) {
      filename = match[1];
    }
  }

  const blobUrl = window.URL.createObjectURL(blob);
  const link = document.createElement("a");

  link.href = blobUrl;
  link.download = filename;
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
  window.URL.revokeObjectURL(blobUrl);
}
